import { useEffect, useLayoutEffect, useRef } from 'react'
import useStore, { startingIndex } from 'state/knovStore'
import {
    getCenterIndex,
    slideTo,
    slidePrev,
    slideNext,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import { isMobile } from 'lib/device'

export default function useSlideEffects() {
    const numPanels = useStore(state => state.numPanels)
    const panels = useStore(store => store.panels.state)
    const lastAction = useStore(store => store.panels.lastAction)
    const lastInsertedPanel = lastAction?.insert
    const animatedPanelInsert = useStore(store => store.panels.animatedPanelInsert)
    const animatedPanelRemove = useStore(store => store.panels.animatedPanelRemove)

    const getIndexById = useStore(store => store.panels.getIndexById)
    const getPanels = useStore(store => store.panels.getPanels)

    const insertWasAnimated = useRef<'left' | 'right' | false>(false)
    useEffect(() => {
        insertWasAnimated.current = animatedPanelInsert?.direction || false
    }, [animatedPanelInsert])

    const prevCenteredPanelId = useRef(panels[startingIndex]?.panelId)
    const updateCenteredPanelId = (newCenteredIndex: number) => {
        //console.log('updating centered panel', newCenteredIndex)
        prevCenteredPanelId.current = getPanels()[newCenteredIndex]?.panelId
    }

    // INSERT (openCenter) SCENARIO: shift center by +1 synchronously (pre-paint)
    useLayoutEffect(
        function slideAfterOpenCenterInsert() {
            if (lastAction?.insert && lastAction?.openCenter) {
                const isOne = isMobile || numPanels === 1
                const prevActive = lastAction?.prevActiveIndex
                const prevCenter = Number.isInteger(prevActive)
                    ? isOne
                        ? (prevActive as number)
                        : (prevActive as number) + 1
                    : getCenterIndex()
                const targetCenter = prevCenter + 1
                // Map logical center to swiper index and slide instantly
                slideTo(targetCenter, 0)
            }
        },
        [lastAction, numPanels],
    )

    // INSERT LEFT (desktop one-panel): the insert put the new panel where the anchor
    // was showing. Keep the anchor on screen for this paint, then slide back to the
    // new panel like Back, so it arrives from the left instead of replacing in place.
    useLayoutEffect(
        function slideInAfterOnePanelInsertLeft() {
            if (!lastAction?.insert || !lastAction?.slideInFromLeft) return
            if (isMobile || numPanels !== 1) return
            const insertIndex = getIndexById(lastAction.insert.panelId)
            if (insertIndex < 0 || getCenterIndex() !== insertIndex) return
            slideTo(insertIndex + 1, 0)
            slideTo(insertIndex)
        },
        [lastAction],
    )

    // REMOVE SCENARIO 3 PANELS
    // 1. Remove the center panel
    // 2. Remove the panel on the left of the center
    // 3. Remove the panel on the right of the center
    // 4. The center panel is no longer in the array, so we need to update the prevCenteredPanelId.
    // 5. When removing panel to the left/in front of center, we slidePrev to keep the center panel consistent since it is now one index less due to removed panel
    // 6. When removing panel on the right side, ie larger indexes, swiper.activeIndex does not change.

    // 6. When removing panel to the right of center, we slidePrev to keep the panel on the left of center in view.

    useLayoutEffect(
        function slideAfterRemove() {
            if (lastAction?.remove) {
                if (lastAction?.suppressSlide) return
                if (lastAction.animated) {
                    if (lastAction.removeIndex < lastAction.centerIndex) {
                        // The direction is always right, and we're always reducing the amount of previous panels, therefore reducing the active/center index.
                        slideTo(lastAction.centerIndex - 1, 0)
                    } else if (lastAction.removeIndex === lastAction.centerIndex) {
                        prevCenteredPanelId.current = lastAction.panelIds[lastAction.centerIndex]
                        if (lastAction.animated === 'left') {
                            slideTo(lastAction.centerIndex, 0)
                        } else {
                            // If the slide direction is right, it means we are on the last panel and the indexing will decrease by 1.

                            // This should be where we handle the mobile case, since mobile is always removing the center panel.
                            slideTo(lastAction.centerIndex - 1, 0)
                        }
                    } else {
                        // The direction is always left, and the amount of previous panels stays the same and therefore so does the index.
                        slideTo(lastAction.centerIndex, 0)
                    }
                } else {
                    // If the action is not animated this means we are removing panels off screen to the left (toggling parent quest) or right (toggling child quest).
                    if (lastAction.removeIndex < lastAction.centerIndex) {
                        slideTo(lastAction.centerIndex - 1, 0)
                    } else {
                        slideTo(lastAction.centerIndex, 0)
                    }
                }
            }
        },
        [lastAction],
    )

    // Removed slide-after-insert. All insert flows must slide explicitly via orchestrators.

    return {
        updateCenteredPanelId,
    }
}
